import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const EncounterCard = ({ encounter, onConfirm, onModify, onFeedback }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'completed':
        return 'bg-success/10 text-success border-success/20';
      case 'pending-feedback':
        return 'bg-warning/10 text-warning border-warning/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'upcoming':
        return 'Upcoming';
      case 'completed':
        return 'Completed';
      case 'pending-feedback':
        return 'Awaiting Feedback';
      default:
        return 'Unknown';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div className="bg-card border border-border rounded-md p-4 md:p-6 shadow-therapeutic transition-therapeutic hover:shadow-therapeutic-lg">
      <div className="flex flex-col space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <span className={`px-3 py-1 rounded-md text-xs font-medium font-caption border ${getStatusColor(encounter?.status)}`}>
                {getStatusLabel(encounter?.status)}
              </span>
              {encounter?.isConfirmed && (
                <span className="flex items-center space-x-1 text-success text-xs font-medium">
                  <Icon name="CheckCircle" size={14} color="var(--color-success)" />
                  <span>Confirmed</span>
                </span>
              )}
            </div>
            <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-1">
              {encounter?.title}
            </h3>
            <div className="flex flex-col space-y-1 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="Calendar" size={16} color="currentColor" />
                <span>{formatDate(encounter?.date)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} color="currentColor" />
                <span>{formatTime(encounter?.time)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border-2 border-primary/20">
              <Image 
                src={encounter?.partner1Avatar} 
                alt={encounter?.partner1AvatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border-2 border-accent/20">
              <Image 
                src={encounter?.partner2Avatar} 
                alt={encounter?.partner2AvatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <div className="bg-muted/50 rounded-md p-3">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Heart" size={16} color="var(--color-accent)" />
              <span className="text-xs font-medium font-caption text-muted-foreground uppercase">Type</span>
            </div>
            <p className="text-sm text-foreground font-medium">{encounter?.type}</p>
          </div>
          <div className="bg-muted/50 rounded-md p-3">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Sparkles" size={16} color="var(--color-primary)" />
              <span className="text-xs font-medium font-caption text-muted-foreground uppercase">Mood Match</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-background rounded-full h-2 overflow-hidden">
                <div 
                  className="bg-primary h-full transition-therapeutic" 
                  style={{ width: `${encounter?.moodMatch}%` }}
                />
              </div>
              <span className="text-sm text-foreground font-medium">{encounter?.moodMatch}%</span>
            </div>
          </div>
        </div>

        {encounter?.aiSuggestions && encounter?.aiSuggestions?.length > 0 && (
          <div className="bg-primary/5 border border-primary/10 rounded-md p-3 md:p-4">
            <div className="flex items-start space-x-2 mb-2">
              <Icon name="Lightbulb" size={18} color="var(--color-primary)" />
              <h4 className="text-sm font-medium font-caption text-foreground">AI Preparation Tips</h4>
            </div>
            <ul className="space-y-1.5 ml-6">
              {encounter?.aiSuggestions?.map((suggestion, index) => (
                <li key={index} className="text-sm text-muted-foreground list-disc">
                  {suggestion}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          {encounter?.status === 'upcoming' && !encounter?.isConfirmed && (
            <>
              <Button 
                variant="default" 
                iconName="Check" 
                iconPosition="left"
                onClick={() => onConfirm(encounter?.id)}
                className="flex-1"
              >
                Confirm Encounter
              </Button>
              <Button 
                variant="outline" 
                iconName="Edit" 
                iconPosition="left"
                onClick={() => onModify(encounter?.id)}
                className="flex-1"
              >
                Modify
              </Button>
            </>
          )}
          {encounter?.status === 'pending-feedback' && (
            <Button 
              variant="default" 
              iconName="MessageSquare" 
              iconPosition="left"
              onClick={() => onFeedback(encounter?.id)}
              fullWidth
            >
              Provide Feedback
            </Button>
          )}
          {encounter?.status === 'completed' && (
            <Button 
              variant="outline" 
              iconName="Eye" 
              iconPosition="left"
              onClick={() => onFeedback(encounter?.id)}
              fullWidth
            >
              View Feedback
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EncounterCard;